import Select from 'components/common/Select/Select';
import {
  ageMaxOptions,
  ageMinOptions,
  useGenderOptions,
  getPmax2RtbAgeGroupsByAgeRange
} from 'core/limitation/l2ObjectTAOptions';
import { FinalReportGender, Order } from 'core/order/Order';
import {
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  RTBCAMPAIGN_DEFAULT_AGE_MIN
} from 'core/rtbCampaign/RtbCampaign';
import { defaultTo, get, remove } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { Form, Row } from 'react-bootstrap';
import i18n from 'i18n';
import styles from './rtbAgeAndGender.module.scss';
import { LimitationData } from 'core/limitation/Limitation';

export const RtbAgeAndGender: React.FC<{
  limitationValueSet: LimitationData;
  onChange: (operate: string, type: string, value: any) => void;
}> = ({ limitationValueSet, onChange }) => {
  const genderOptions = useGenderOptions();

  const getOptValueOfTA = useCallback((includeTA) => {
    return includeTA ? 'include' : 'preferred';
  }, []);

  const generalTAInclude = defaultTo(limitationValueSet['include'], []);
  const generalTAPreferred = defaultTo(limitationValueSet['preferred'], []);
  const ageMinInclude = generalTAInclude.find(
    (value) => value.type === 'age_min'
  );
  const ageMinPreferred = generalTAPreferred.find(
    (value) => value.type === 'age_min'
  );
  const ageMaxInclude = generalTAInclude.find(
    (value) => value.type === 'age_max'
  );
  const ageMaxPreferred = generalTAPreferred.find(
    (value) => value.type === 'age_max'
  );
  const genderInclude = generalTAInclude.find(
    (value) => value.type === 'gender'
  );
  const genderPerferred = generalTAPreferred.find(
    (value) => value.type === 'gender'
  );

  const ageOptValue = getOptValueOfTA(ageMinInclude);
  const genderOptValue = getOptValueOfTA(genderInclude);

  const ageMinLimitation = ageMinInclude || ageMinPreferred;
  const ageMin = get(ageMinLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MIN);

  const ageMaxLimitation = ageMaxInclude || ageMaxPreferred;
  const ageMax = get(ageMaxLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MAX);

  const gendersLimitation = genderInclude || genderPerferred;
  const gender = get(
    gendersLimitation,
    'value[0]',
    genderOptions.find((option) => option.value === FinalReportGender.ALL)
  );

  const onAgeMinChange = useCallback(
    (newAgeMin) => {
      if (newAgeMin > ageMax) {
        onChange(ageOptValue, 'age_max', newAgeMin);
      }
      onChange(ageOptValue, 'age_min', newAgeMin);
    },
    [ageOptValue, ageMax, onChange]
  );
  const onAgeMaxChange = useCallback(
    (newAgeMax) => {
      if (newAgeMax < ageMin) {
        onChange(ageOptValue, 'age_min', newAgeMax);
      }
      onChange(ageOptValue, 'age_max', newAgeMax);
    },
    [ageOptValue, ageMin, onChange]
  );
  const onAgeOptChange = useCallback(
    (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, (opt) => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'age_min', ageMin);
      onChange(optToSetValue, 'age_max', ageMax);
      onChange(optToSetUndefined, 'age_min', undefined);
      onChange(optToSetUndefined, 'age_max', undefined);
    },
    [ageMin, ageMax, onChange]
  );

  const getGenderValue = useCallback(
    (gender) => (gender.value === -1 ? undefined : [gender]),
    []
  );
  const onGendersChange = useCallback(
    (gender) => {
      onChange(genderOptValue, 'gender', getGenderValue(gender));
    },
    [genderOptValue, onChange, getGenderValue]
  );
  const onGenderOptChange = useCallback(
    (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, (opt) => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'gender', getGenderValue(gender));
      onChange(optToSetUndefined, 'gender', undefined);
    },
    [gender, getGenderValue, onChange]
  );

  const ageGroupsHint = useMemo(() => {
    return getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
      .map((group) => group.label)
      .join(',');
  }, [ageMin, ageMax]);

  return (
    <div>
      <Form.Group as={Row} controlId={'age'}>
        <Form.Label>{i18n.t('limitation.labels.age')}</Form.Label>
        <div>
          <div className={styles.ageSelector}>
            <Select
              options={ageMinOptions}
              name='ageMin'
              simpleValue
              value={ageMin}
              onChange={onAgeMinChange}
            />
            ~
            <Select
              options={ageMaxOptions}
              name='ageMax'
              simpleValue
              value={ageMax}
              onChange={onAgeMaxChange}
            />
          </div>
          <span className={styles.ageGroupsHint}>
            {i18n.t('limitation.hints.ageGroups', {
              groups: ageGroupsHint
            })}
          </span>
        </div>
      </Form.Group>
      <Fragment>
        <Form.Group
          as={Row}
          controlId={'age-opt-preferred'}
          style={{ marginBottom: 0 }}
        >
          <Form.Label>{''}</Form.Label>
          <div className={styles.optRadio}>
            <Form.Check
              type={'radio'}
              label={i18n.t('rtbCampaign.limitation.operationSet.preferred')}
              value={'preferred'}
              id={'age_preferred'}
              onChange={onAgeOptChange}
              checked={ageOptValue === 'preferred'}
              disabled={false}
            />
          </div>
        </Form.Group>
        <Form.Group as={Row} controlId={'age-opt-include'}>
          <Form.Label>{''}</Form.Label>
          <div className={styles.optRadio}>
            <Form.Check
              type={'radio'}
              label={i18n.t('rtbCampaign.limitation.operationSet.include')}
              value={'include'}
              id={'age_include'}
              onChange={onAgeOptChange}
              checked={ageOptValue === 'include'}
              disabled={false}
            />
          </div>
        </Form.Group>
      </Fragment>
      <Form.Group as={Row} controlId={'gender'}>
        <Form.Label>{i18n.t('limitation.labels.gender')}</Form.Label>
        <Select
          options={genderOptions}
          value={gender}
          onChange={onGendersChange}
        />
      </Form.Group>
      {gender &&
        typeof gender === 'object' &&
        // TS2322 'value' in gender
        // gender 的型別包含 string（來自 Limitation.value 的 union type），而 in 運算子要求右側是 object。加了 typeof gender === 'object' 的 type guard。
        'value' in gender &&
        gender.value !== -1 && (
          <Fragment>
            <Form.Group
              as={Row}
              controlId={'gender-opt-preferred'}
              style={{ marginBottom: 0 }}
            >
              <Form.Label>{''}</Form.Label>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t(
                    'rtbCampaign.limitation.operationSet.preferred'
                  )}
                  value={'preferred'}
                  id={'gender_preferred'}
                  onChange={onGenderOptChange}
                  checked={genderOptValue === 'preferred'}
                  disabled={false}
                />
              </div>
            </Form.Group>
            <Form.Group as={Row} controlId={'gender-opt-include'}>
              <Form.Label>{''}</Form.Label>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t('rtbCampaign.limitation.operationSet.include')}
                  value={'include'}
                  id={'gender_include'}
                  onChange={onGenderOptChange}
                  checked={genderOptValue === 'include'}
                  disabled={false}
                />
              </div>
            </Form.Group>
          </Fragment>
        )}
    </div>
  );
};

export const renderAgeAndGender = (
  limitationValueSet: LimitationData,
  _order: Order,
  onChange: (operate: string, type: string, value: any) => void
) => {
  return (
    <RtbAgeAndGender
      limitationValueSet={limitationValueSet}
      onChange={onChange}
    />
  );
};
